import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { translations } from "../i18n/translations";
import api from "../lib/api";

const AppContext = createContext(null);

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};

export const AppProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("scoutos_lang") || "en");
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [watchlist, setWatchlist] = useState([]);

  useEffect(() => {
    localStorage.setItem("scoutos_lang", lang);
  }, [lang]);

  const t = translations[lang] || translations.en;

  const fetchWatchlist = useCallback(async () => {
    try {
      const { data } = await api.get("/watchlist");
      setWatchlist(data || []);
    } catch {
      setWatchlist([]);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await fetchWatchlist();
    } catch {
      setUser(null);
      setWatchlist([]);
    } finally {
      setAuthLoading(false);
    }
  }, [fetchWatchlist]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setAuthLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = useCallback(() => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(null);
    setWatchlist([]);
  }, []);

  const setUserAfterAuth = useCallback((u) => {
    setUser(u);
    fetchWatchlist();
  }, [fetchWatchlist]);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {}
  }, []);

  const setRole = useCallback(async (role) => {
    const { data } = await api.patch("/auth/role", { role });
    setUser(data);
    return data;
  }, []);

  const toggleWatch = useCallback(async (playerId) => {
    if (!user) {
      loginWithGoogle();
      return;
    }
    try {
      const { data } = await api.post(`/watchlist/${playerId}`);
      setWatchlist(data || []);
    } catch {}
  }, [user, loginWithGoogle]);

  const value = {
    lang,
    setLang,
    t,
    user,
    authLoading,
    loginWithGoogle,
    logout,
    setUserAfterAuth,
    refreshUser,
    setRole,
    hasRole: !!(user && ["player", "academy_agent", "pro_team"].includes(user.role)),
    isBusiness: !!(user && ["academy_agent", "pro_team"].includes(user.role)),
    isAdmin: !!(user && user.is_admin),
    hasActiveSub: !!(user && user.subscription && ["trialing", "active"].includes(user.subscription.status)),
    watchlist,
    toggleWatch,
    isInWatch: (id) => watchlist.includes(id),
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
